/**
 * @fileoverview In-memory store for the notification preferences mock.
 * @description Seeds a few categories and notification types across EMAIL / PUSH / IN_APP and keeps
 * the user's saved choices for the session, with a per-row version that becomes the opaque
 * `rowVersion` (base64, null until a row has been saved once). Applying a batch is atomic: every
 * change is validated first (unknown target, not configurable, stale rowVersion) and nothing is
 * stored unless all pass, like the real PUT.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import type {
  NotificationPreference,
  NotificationPreferenceScope,
  NotificationPreferenceUpdate,
} from '@/features/notifications/services/notification-preferences.service'

interface SeedTarget {
  scope: NotificationPreferenceScope
  key: string
  isConfigurable: boolean
  isMandatory?: boolean
  defaults: { EMAIL: boolean; PUSH: boolean; IN_APP: boolean }
}

interface SavedState {
  value: boolean
  version: number
}

export type PreferenceUpdateFailure = 'NOT_FOUND' | 'NOT_CONFIGURABLE' | 'STALE_VERSION'

const CHANNELS = ['EMAIL', 'PUSH', 'IN_APP'] as const

const SEED_TARGETS: SeedTarget[] = [
  {
    scope: 'CATEGORY',
    key: 'SALES',
    isConfigurable: true,
    defaults: { EMAIL: true, PUSH: true, IN_APP: true },
  },
  {
    scope: 'CATEGORY',
    key: 'SYSTEM',
    isConfigurable: true,
    defaults: { EMAIL: false, PUSH: false, IN_APP: true },
  },
  {
    scope: 'TYPE',
    key: 'PROSPECT_ASSIGNED',
    isConfigurable: true,
    isMandatory: false,
    defaults: { EMAIL: true, PUSH: true, IN_APP: true },
  },
  {
    scope: 'TYPE',
    key: 'TARGET_ASSIGNED',
    isConfigurable: true,
    isMandatory: false,
    defaults: { EMAIL: true, PUSH: false, IN_APP: true },
  },
  {
    scope: 'TYPE',
    key: 'SECURITY_ALERT',
    isConfigurable: false,
    isMandatory: true,
    defaults: { EMAIL: true, PUSH: true, IN_APP: true },
  },
]

const saved = new Map<string, SavedState>()

/**
 * @description The map key of one target + channel.
 * @param {string} scope - TYPE or CATEGORY.
 * @param {string} key - The type or category code.
 * @param {string} channel - The channel.
 * @returns {string} The key.
 */
function stateKey(scope: string, key: string, channel: string): string {
  return `${scope}:${key}:${channel}`
}

/**
 * @description The opaque version token for a saved row; null when never saved.
 * @param {SavedState | undefined} state - The row's saved state.
 * @returns {string | null} The rowVersion.
 */
function toRowVersion(state: SavedState | undefined): string | null {
  return state ? btoa(`v${state.version}`) : null
}

/**
 * @description Lists every preference row the way GET /notifications/preferences returns it.
 * @returns {NotificationPreference[]} One row per target and channel.
 */
export function listPreferences(): NotificationPreference[] {
  return SEED_TARGETS.flatMap((target) =>
    CHANNELS.map((channel) => {
      const state = saved.get(stateKey(target.scope, target.key, channel))
      return {
        preferenceId: state ? `pref-${target.scope}-${target.key}-${channel}` : null,
        preferenceScope: target.scope,
        ...(target.scope === 'TYPE' ? { notificationType: target.key } : { category: target.key }),
        channel,
        userPreference: state ? state.value : null,
        effectiveEnabled: state ? state.value : target.defaults[channel],
        ...(target.scope === 'TYPE' ? { isMandatory: target.isMandatory === true } : {}),
        isConfigurable: target.isConfigurable,
        source: state ? 'USER' : 'DEFAULT',
        overrideSource: null,
        effectiveFrom: null,
        rowVersion: toRowVersion(state),
      }
    }),
  )
}

/**
 * @description Finds why one change would be rejected, if it would.
 * @param {NotificationPreferenceUpdate} update - The change.
 * @returns {PreferenceUpdateFailure | null} The failure, or null when it can be applied.
 */
function findFailure(update: NotificationPreferenceUpdate): PreferenceUpdateFailure | null {
  const key = update.preferenceScope === 'TYPE' ? update.notificationType : update.category
  const target = SEED_TARGETS.find(
    (seed) => seed.scope === update.preferenceScope && seed.key === key,
  )
  if (!target || !CHANNELS.some((channel) => channel === update.channel)) return 'NOT_FOUND'
  if (!target.isConfigurable) return 'NOT_CONFIGURABLE'
  const state = saved.get(stateKey(target.scope, target.key, update.channel))
  return state && update.rowVersion !== toRowVersion(state) ? 'STALE_VERSION' : null
}

/**
 * @description Applies a batch of changes atomically.
 * @param {NotificationPreferenceUpdate[]} updates - The changes.
 * @returns {PreferenceUpdateFailure | null} The first failure (nothing applied), or null on success.
 */
export function applyPreferenceUpdates(
  updates: NotificationPreferenceUpdate[],
): PreferenceUpdateFailure | null {
  for (const update of updates) {
    const failure = findFailure(update)
    if (failure) return failure
  }

  for (const update of updates) {
    const key =
      (update.preferenceScope === 'TYPE' ? update.notificationType : update.category) ?? ''
    const id = stateKey(update.preferenceScope, key, update.channel)
    if (update.isEnabled === null) {
      saved.delete(id)
    } else {
      saved.set(id, { value: update.isEnabled, version: (saved.get(id)?.version ?? 0) + 1 })
    }
  }
  return null
}
